import { useState, useEffect } from 'react';
import { useToast } from './use-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../config/api';

/**
 * Shared bookmark toggle logic for BookCard and BookDetailsModal.
 *
 * Manages:
 *   - isSaved state (optionally synced from parent via initialSaved)
 *   - toggle() action that add/removes from library (source: 'saved')
 *
 * Options:
 *   - skipLibraryCheck: if true, don't hit /api/library on mount to check status.
 *     Parents that already know the saved state should set this.
 *   - onChanged(bookId, isSaved): callback for parent to sync its own state.
 */
export function useBookmark(book, { initialSaved = false, skipLibraryCheck = false, onChanged } = {}) {
  const { toast } = useToast();
  const { user, token } = useAuth();
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [isSaving, setIsSaving] = useState(false);

  // Check library on mount (unless parent opts out)
  useEffect(() => {
    if (skipLibraryCheck) {
      setIsSaved(initialSaved);
      return;
    }

    async function checkIfSaved() {
      if (!user || !token) return;
      try {
        const response = await fetch(apiUrl('/api/library'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setIsSaved(data.books?.some(b => b.book_id === book.id && b.source === 'saved') || false);
        }
      } catch (error) {
        console.error('[useBookmark] Failed to check library status:', error);
      }
    }

    checkIfSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, book.id, skipLibraryCheck]);

  const toggle = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();

    if (!user) {
      toast({
        variant: "destructive",
        title: "Not logged in",
        description: "Please log in to save books to your library.",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (isSaved) {
        const response = await fetch(apiUrl('/api/library/remove'), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ book_id: book.id, source: 'saved' })
        });
        if (response.ok) {
          setIsSaved(false);
          toast({ title: "Removed", description: "Book removed from your library." });
          if (onChanged) onChanged(book.id, false);
        } else {
          const errData = await response.json().catch(() => ({}));
          toast({ variant: "destructive", title: "Error", description: errData.error || "Failed to remove book." });
        }
      } else {
        const response = await fetch(apiUrl('/api/library/add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ book_id: book.id, source: 'saved' })
        });
        if (response.ok) {
          setIsSaved(true);
          toast({ title: "Saved", description: "Book added to your library." });
          if (onChanged) onChanged(book.id, true);
        } else {
          const errData = await response.json().catch(() => ({}));
          toast({ variant: "destructive", title: "Error", description: errData.error || "Failed to save book." });
        }
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to update library." });
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaved, isSaving, toggle, setIsSaved };
}
